import { CubeTransparentIcon, ExclamationTriangleIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';
import useOpen from '../../../hooks/useOpen';
import { BuyCreditsModal } from '../../organisation/BuyCreditsModal';
import { ContactSalesModal } from '../../organisation/ContactSalesModal';
import { memo, useCallback, useContext, useState } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { UserContext } from '../../../contexts/UserContext';
import { useRouteParams } from '../../../hooks/useRouteParams';
import { T } from '../../../translation/src';
import { Permissions, Project, UploadStatus, useOrganisationsQuery, useProjectChargeCreditsMutation, useSuperadminUnlockProjectMutation } from '../../../types/graphqlTypes';

interface ChargeCreditsModalProps {
  onClose: () => void;
  open?: boolean;
  project: Pick<Project, 'id' | 'name' | 'creditsOutstanding' | 'creditBreakdown'>;
}

const pointsFormat = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 });
const formatPoints = (points: number) => pointsFormat.format(points);

const BalanceFigure: React.FC<{ value: number; label: React.ReactNode; className?: string }> = ({ value, label, className }) => (
  <div className={classNames('flex flex-col items-center gap-1', className)}>
    <span className="text-3xl font-semibold tabular-nums leading-none">{value}</span>
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
  </div>
);

// Confirmation dialog for AWAITING_CREDITS projects: shows the outstanding cost
// against the organisation balance and charges + activates on confirm.
// Declining just closes — the project stays parked and chargeable later.
const ChargeCreditsModal_: React.FC2<ChargeCreditsModalProps> = ({ onClose, open, project }) => {
  const { organisationId } = useRouteParams(['organisationId']);
  const currentUser = useContext(UserContext);
  // The organisations query carries credits (unlike the user-context organisation).
  const organisationsQuery = useOrganisationsQuery({ variables: { includeIds: [organisationId] } });
  const currentOrganisation = organisationsQuery.data?.organisations?.find((organisation) => organisation.id === organisationId);
  const [chargeCredits, { loading }] = useProjectChargeCreditsMutation();
  // The superadmin's way round the balance: unlocked for free, the organisation is not charged.
  const [unlockProject, { loading: unlocking }] = useSuperadminUnlockProjectMutation();
  const [serverBalanceShort, setServerBalanceShort] = useState(false);
  // The server refused: the subscription is paused (from Odoo) — nothing is spent until it resumes.
  const [serverPaused, setServerPaused] = useState(false);

  // Known before asking: the organisation says so itself; the server's refusal covers a stale page.
  const paused = serverPaused || !!currentOrganisation?.subscription.paused;
  const balance = currentOrganisation?.credits?.balance ?? 0;
  const required = project.creditsOutstanding;
  const insufficientBalance = serverBalanceShort || balance < required;
  const breakdown = project.creditBreakdown;
  // Over the single-project limit: the reason it costs more than one.
  const isLarge = breakdown.pointCount > breakdown.singleProjectMaxPoints;
  // Part of the tier settled before (e.g. converted as one project, then grown past the limit).
  const alreadyPaid = Math.max(0, breakdown.required - required);
  const canSeeSubscription = currentUser.isSuperAdmin || (currentOrganisation?.myPermissions || []).some((permission) => permission === Permissions.All || permission === Permissions.Billing);
  // Billed to a partner, the organisation buys nothing itself: its partner does.
  const canBuyCredits = !currentOrganisation?.referral.billedTo && (currentUser.isSuperAdmin || (currentOrganisation?.myPermissions || []).some((permission) => permission === Permissions.All || permission === Permissions.Organisations));

  const onConfirm = useCallback(async () => {
    try {
      await chargeCredits({
        variables: { projectId: project.id },
        refetchQueries: ['organisations', 'currentUser'],
        update: (cache) => {
          cache.modify({
            id: `Project:${project.id}`,
            fields: {
              uploadStatus: () => UploadStatus.Success,
              creditsOutstanding: () => 0,
            },
          });
        },
      });
      onClose();
    } catch (error) {
      const code = graphqlErrorCode(error);
      if (code === 'INSUFFICIENT_CREDITS') {
        setServerBalanceShort(true);
        return;
      }
      if (code === 'SUBSCRIPTION_PAUSED') {
        setServerPaused(true);
        return;
      }
      console.error(error);
    }
  }, [chargeCredits, onClose, project.id, setServerBalanceShort, setServerPaused]);

  const onUnlock = useCallback(async () => {
    try {
      await unlockProject({
        variables: { projectId: project.id },
        refetchQueries: ['organisations', 'currentUser'],
        update: (cache) => {
          cache.modify({
            id: `Project:${project.id}`,
            fields: {
              uploadStatus: () => UploadStatus.Success,
              creditsOutstanding: () => 0,
            },
          });
        },
      });
      onClose();
    } catch (error) {
      console.error(error);
    }
  }, [onClose, project.id, unlockProject]);

  // Buying on top of this dialog keeps the activation flow alive: the purchase refetches
  // the balance and the footer turns into "activate" without leaving the project.
  const { open: buyOpen, onOpen: openBuy, onClose: closeBuy } = useOpen();
  const onCloseBuy = useCallback(() => {
    // The stale server verdict would otherwise keep the footer on "buy projects" forever.
    setServerBalanceShort(false);
    closeBuy();
  }, [closeBuy, setServerBalanceShort]);
  const { open: contactOpen, onOpen: openContact, onClose: closeContact } = useOpen();

  return (
    <>
      <Modal.Container onClose={onClose} open={open} panelClassName={classNames('w-full', currentUser.isSuperAdmin ? 'max-w-xl' : 'max-w-md')}>
        <Modal.Header onClose={onClose}>
          <T _str="Activate {name}" args={{ name: project.name }} />
        </Modal.Header>
        <Modal.Body className="flex flex-col gap-5 text-sm">
          {/* Why it costs what it costs, first: the reason, then the sum. */}
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-french-violet-500/15 text-french-violet-600 dark:text-french-violet-300">
              {isLarge ? <CubeTransparentIcon className="h-6 w-6" /> : <LockOpenIcon className="h-6 w-6" />}
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-medium text-black dark:text-white">
                {isLarge ? <T _str="large point cloud · {points} points" args={{ points: formatPoints(breakdown.pointCount) }} swc /> : <T _str="ready to activate" swc />}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {isLarge ? (
                  <T _str="Point clouds over {limit} points count as {required} projects." args={{ limit: formatPoints(breakdown.singleProjectMaxPoints), required: breakdown.required }} />
                ) : (
                  <T _str="Activating it takes projects off your organisation's balance." />
                )}
              </span>
            </div>
          </div>
          {/* The balance as one sum: what the organisation has, what this project takes, what is left. */}
          <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center rounded-xl bg-gray-100 px-4 py-5 text-center dark:bg-white/5">
            <BalanceFigure value={balance} label={<T _str="available" swc />} />
            <span className="text-2xl font-light text-gray-400">−</span>
            <BalanceFigure value={required} label={alreadyPaid > 0 ? <T _str="this project ({paid} paid)" args={{ paid: alreadyPaid }} swc /> : <T _str="this project" swc />} className="text-french-violet-500 dark:text-french-violet-300" />
            <span className="text-2xl font-light text-gray-400">=</span>
            {insufficientBalance ? <BalanceFigure value={required - balance} label={<T _str="short" swc />} className="text-amber-600 dark:text-amber-400" /> : <BalanceFigure value={balance - required} label={<T _str="left" swc />} />}
          </div>
          {(paused || insufficientBalance) && (
            <p className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              <span>
                {paused ? (
                  <T _str="Your subscription is paused: nothing is activated until it resumes. Contact us with any questions." />
                ) : canBuyCredits ? (
                  <T _str="Not enough projects available. Buy projects to activate it." />
                ) : (
                  <T _str="Not enough projects available. Ask your organisation admin to add projects, or contact us." />
                )}
              </span>
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="gap-3 text-base">
          {canSeeSubscription && (
            <Link to={`/organisations/${organisationId}/subscription`} onClick={onClose} className="mr-auto whitespace-nowrap text-sm text-neon-green-600 hover:underline dark:text-neon-green-400">
              <T _str="view subscription" swc /> →
            </Link>
          )}
          <button type="button" onClick={onClose} className={classNames('whitespace-nowrap px-3 text-sm text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white', !canSeeSubscription && 'ml-auto')}>
            <T _str="not now" swc />
          </button>
          {currentUser.isSuperAdmin && (
            // Beside whatever the organisation may do: free, and whatever the balance or the pause says.
            <Button variant="superAdmin" size="sm" className="whitespace-nowrap" onClick={onUnlock} loading={unlocking} disabled={loading}>
              <T _str="unlock for free" swc />
            </Button>
          )}
          {paused ? (
            <Button variant="primary" size="sm" className="whitespace-nowrap" onClick={openContact}>
              <T _str="contact us" swc />
            </Button>
          ) : insufficientBalance ? (
            canBuyCredits ? (
              <Button variant="primary" size="sm" className="whitespace-nowrap" onClick={openBuy}>
                <T _str="buy projects" swc />
              </Button>
            ) : (
              <Button variant="primary" size="sm" className="whitespace-nowrap" onClick={openContact}>
                <T _str="contact us" swc />
              </Button>
            )
          ) : (
            <Button variant="primary" size="sm" className="whitespace-nowrap" onClick={onConfirm} disabled={loading}>
              <T _str="activate" swc />
            </Button>
          )}
        </Modal.Footer>
      </Modal.Container>
      {canBuyCredits && currentOrganisation && (
        <BuyCreditsModal
          open={buyOpen}
          onClose={onCloseBuy}
          organisationId={organisationId}
          projectUnitPrice={currentOrganisation.subscription.projectUnitPrice}
          minAmount={currentOrganisation.subscription.minPurchaseAmount}
          maxAmount={currentOrganisation.subscription.maxPurchaseAmount}
        />
      )}
      <ContactSalesModal open={contactOpen} onClose={closeContact} organisationId={organisationId} />
    </>
  );
};

export const ChargeCreditsModal = memo(ChargeCreditsModal_);
